// The BYO log sink's state (#448), lifted out of the `AccountKeys` container the
// way `createKeyRotate` and `createRouteEditor` were — one more thing that page
// held only because it happened to render the card.
//
// NOT `createMutation`, and the reason is the one that file's header already
// names as its exception: these two writes REPLACE the server's sentence with
// client copy chosen by HTTP STATUS (400 = the URL failed validation, 422 = we
// could not connect to it), which neither kit expresses. Closing that is a COPY
// decision about whether these screens should keep substituting at all — not a
// knob to bolt onto the kit — so the hand-rolled ladder moves here intact.
//
// Both writes re-read INSIDE the pending window (`finally`, refetch above the
// `setBusy(false)`), which is the ordering the whole app holds: releasing the
// control earlier offers a second click over a card the screen has not
// corrected yet.

import { createSignal } from 'solid-js';
import { t } from '../i18n';
import { ApiError } from './api';
import { clearLogSink, setLogSink, useLogSink } from './log-sink';

export type LogSinkEditor = {
  readonly status: ReturnType<typeof useLogSink>;
  readonly busy: () => boolean;
  readonly error: () => string | null;
  readonly save: (url: string) => void;
  readonly clear: () => void;
};

export function createLogSinkEditor(token: string | null): LogSinkEditor {
  const status = useLogSink(token);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  /** The 400/422 split, which is the whole reason this is not `createMutation`. */
  const saveFailure = (e: unknown): string => {
    if (e instanceof ApiError && e.status === 400) return t('log_sink.invalid_error');
    if (e instanceof ApiError && e.status === 422) return t('log_sink.connect_error');
    return t('log_sink.save_error');
  };

  const run = async (call: () => Promise<unknown>, failure: (e: unknown) => string) => {
    setBusy(true);
    setError(null);
    try {
      await call();
    } catch (e) {
      setError(failure(e));
    } finally {
      await status.refetch();
      setBusy(false);
    }
  };

  return {
    status,
    busy,
    error,
    save: (url) => void run(() => setLogSink(url, token), saveFailure),
    clear: () =>
      void run(
        () => clearLogSink(token),
        () => t('log_sink.clear_error'),
      ),
  };
}
